/**
 * Personal Mode Backup/Restore Limit Message
 *
 * Shows a message when Personal mode restricts backup/restore due to large database.
 * Explains the limit and provides an upgrade option.
 */

import { AlertTriangle, Sparkles, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PersonalModeLimitCheck } from '../utils/personalModeLimits';
import { PERSONAL_MODE_MAX_SIZE_BYTES, formatBytes } from '../utils/personalModeLimits';

type PersonalModeBackupRestoreLimitMessageProps = {
  limitCheck: PersonalModeLimitCheck;
  operationType: 'backup' | 'export' | 'restore' | 'import';
  onUpgrade: () => void;
  onCancel: () => void;
};

/**
 * Message shown when database exceeds Personal mode size limits.
 * Offers upgrade prompt and cancel option.
 */
export function PersonalModeBackupRestoreLimitMessage({
  limitCheck,
  operationType,
  onUpgrade,
  onCancel,
}: PersonalModeBackupRestoreLimitMessageProps) {
  const operationLabel = getOperationLabel(operationType);
  const limitString = formatBytes(PERSONAL_MODE_MAX_SIZE_BYTES);

  return (
    <div className="flex flex-col items-center justify-center p-6" data-testid="personal-mode-backup-restore-limit">
      <div className="max-w-md text-center space-y-5">
        {/* Icon */}
        <div className="flex justify-center">
          <div className="p-3 rounded-full bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
            <AlertTriangle className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
          </div>
        </div>

        {/* Title */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">
            Large Database Detected
          </h3>
          <p className="text-sm text-muted-foreground">
            {limitCheck.description}
          </p>
        </div>

        {/* Limit explanation */}
        <div className="p-3 bg-muted/30 rounded-lg border border-border">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Database className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              Personal Mode Limit
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Personal mode limits {operationLabel} to databases {limitString} or smaller.
            Upgrade to Pro for unlimited database size support.
          </p>
        </div>

        {/* Upgrade prompt */}
        <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">
              Upgrade to Pro
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            {operationLabel.charAt(0).toUpperCase() + operationLabel.slice(1)} databases of any size with Pro.
          </p>
          <div className="flex gap-2 justify-center">
            <Button
              variant="outline"
              onClick={onCancel}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button onClick={onUpgrade} className="gap-2" data-testid="upgrade-button">
              <Sparkles className="h-4 w-4" />
              Upgrade to Pro
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Get a user-friendly label for the operation type.
 */
function getOperationLabel(operationType: 'backup' | 'export' | 'restore' | 'import'): string {
  switch (operationType) {
    case 'backup':
      return 'backup';
    case 'export':
      return 'export';
    case 'restore':
      return 'restore';
    case 'import':
      return 'import';
    default:
      return 'backup/restore';
  }
}
